import React, { useState, useEffect, useRef } from 'react';
import { handleAppDownload } from '../../../utils/functions';
import apiService from '../../../services/api';

const LiveOffersModule = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const isHoveredRef = useRef(false);
  /** Avoid firing smooth scroll-to-top every tick while still at bottom (caused stacked animations / jumps). */
  const isResettingToTopRef = useRef(false);

  useEffect(() => {
    loadLiveRates();
    // Refresh rates every 30 seconds
    const interval = setInterval(loadLiveRates, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Start auto-scroll when rates are loaded
    if (Object.keys(rates).length > 0 && scrollContainerRef.current) {
      startAutoScroll();
    }
    return () => {
      stopAutoScroll();
    };
  }, [rates]);

  const startAutoScroll = () => {
    stopAutoScroll(); // Clear any existing interval
    isResettingToTopRef.current = false;

    scrollIntervalRef.current = setInterval(() => {
      if (scrollContainerRef.current && !isHoveredRef.current) {
        const container = scrollContainerRef.current;
        const scrollAmount = 1; // Pixels to scroll per interval
        const { scrollTop, clientHeight, scrollHeight } = container;

        if (scrollHeight <= clientHeight + 8) {
          return;
        }

        if (scrollTop <= 2) {
          isResettingToTopRef.current = false;
        }

        const atBottom = scrollTop + clientHeight >= scrollHeight - 5;

        if (atBottom) {
          if (!isResettingToTopRef.current) {
            isResettingToTopRef.current = true;
            container.scrollTo({ top: 0, behavior: 'smooth' });
          }
          return;
        }

        container.scrollBy({ top: scrollAmount, behavior: 'auto' });
      }
    }, 50); // Scroll every 50ms for smooth movement
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    stopAutoScroll();
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
    startAutoScroll();
  };

  const loadLiveRates = async () => {
    try {
      setLoading(true);
      const response = await apiService.getPolymerLiveRate();
      
      // Extract data array from response
      let dataArray = [];
      
      // The apiService already extracts and returns { success: true, data: [...] }
      // So we just need to check response.success and response.data
      if (response && response.success && response.data && Array.isArray(response.data)) {
        dataArray = response.data;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Fallback: if data exists but success flag is missing
        dataArray = response.data;
      } else if (response && response.error) {
        // If there's an error, log it
        console.error('Failed to load polymer live rates:', response.error);
      }
      
      if (dataArray.length > 0) {
        // Transform API response to match UI structure
        const transformedRates = dataArray.map((item, index) => {
          // Build category: "LDPE - Heavy" or "PP - Mould 9-15 Mfi"
          const categoryParts = [];
          if (item.grade_name) categoryParts.push(item.grade_name);
          if (item.process_name) {
            categoryParts.push(item.process_name);
          } else if (item.mfi_value) {
            // If no process_name but has MFI, use MFI in category
            categoryParts.push(item.mfi_value);
          }
          const category = categoryParts.length > 0 ? categoryParts.join(' - ') : 'Polymer';
          
          // Build product name: "Lotrene FE3000" or "DOW 150E" (brand + code)
          const productParts = [];
          if (item.brand_name) productParts.push(item.brand_name);
          if (item.code_name) productParts.push(item.code_name);
          const productName = productParts.length > 0 
            ? productParts.join(' ') 
            : (item.grade_name || 'Product');
          
          // Extract price from total_price field (as per API response)
          const price = parseFloat(item.total_price || item.original_price || 0);
          
          // Extract location: "Aslali, Ahmedabad" or "Bhiwandi, Maharashtra"
          let location = '';
          if (item.area_name && String(item.area_name).trim() !== '') {
            location = String(item.area_name).trim();
            if (item.city_name && String(item.city_name).trim() !== '' && 
                String(item.city_name).trim().toLowerCase() !== location.toLowerCase()) {
              location = location + ', ' + String(item.city_name).trim();
            } else if (item.state_name && String(item.state_name).trim() !== '' && 
                       String(item.state_name).trim().toLowerCase() !== location.toLowerCase()) {
              location = location + ', ' + String(item.state_name).trim();
            }
          } else if (item.city_name && String(item.city_name).trim() !== '') {
            location = String(item.city_name).trim();
            if (item.state_name && String(item.state_name).trim() !== '') {
              location = location + ', ' + String(item.state_name).trim();
            }
          } else if (item.state_name && String(item.state_name).trim() !== '') {
            location = String(item.state_name).trim();
          }
          
          return {
            id: item.id || index,
            category: category,
            product_name: productName,
            price: price,
            location: location || 'Location TBD',
            grade_name: item.grade_name || '',
            process_name: item.process_name || '',
            brand_name: item.brand_name || '',
            code_name: item.code_name || '',
            mfi_value: item.mfi_value || ''
          };
        });
        
        // Group by category
        const groupedRates = transformedRates.reduce((acc, rate) => {
          const category = rate.category;
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(rate);
          return acc;
        }, {});
        
        setRates(groupedRates);
      } else {
        setRates({});
        if (response?.error) {
          console.error('Failed to load polymer live rates:', response.error);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading live rates:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      setLoading(false);
      setRates({});
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'N/A';
    const numPrice = typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : price;
    return numPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="module-card live-offer-module">
      <div className="live-rate-heading">
        <div className="module-icon">
          <i className="fas fa-chart-line"></i>
        </div>
        <h3>Polymer Live Rate</h3>
      </div>
      
      <div 
        className="live-offer-content" 
        id="live-offer-content"
        ref={scrollContainerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ overflowY: 'auto', maxHeight: '400px' }}
      >
        {loading ? (
          <div className="offer-loading">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading rates...</p>
          </div>
        ) : Object.keys(rates).length > 0 ? (
          <div className="live-rates-list">
            {Object.entries(rates).map(([category, categoryRates]) => (
              <div key={category} className="rate-category-group">
                <div className="rate-category-header">{category}</div>
                {categoryRates.map((rate, index) => (
                  <div key={rate.id || index} className="rate-item">
                    <div className="rate-item-content">
                      <div className="rate-item-header">
                        <div className="rate-product-name">{rate.product_name}</div>
                        {rate.price > 0 ? (
                          <div className="rate-price-badge">
                            ₹{formatPrice(rate.price)}
                          </div>
                        ) : (
                          <div className="rate-price-badge">Price on Request</div>
                        )}
                      </div>
                      {rate.location && (
                        <div className="rate-location">
                          <i className="fas fa-map-marker-alt"></i>
                          <span>{rate.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="offer-loading">
            <p>No rates available at the moment.</p>
          </div>
        )}
      </div>
      
      <button 
        className="btn btn-primary btn-download-app module-download-btn" 
        onClick={() => handleAppDownload('buyer')}
      >
        <i className="fas fa-mobile-alt"></i>
        <span>Download App</span>
      </button>
    </div>
  );
};

export default LiveOffersModule;

